// components/StorefrontNav.jsx
import React from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  Sparkles,
  Truck,
  BadgeCheck,
  Clock,
  GraduationCap,
  Boxes,
} from "lucide-react";
import { useTheme } from "../hooks/useTheme";

/**
 * Storefront navigation — pills that switch the shop mode.
 *
 * Lives on every storefront page (marketplace, dropshipping, preorder,
 * courses). Renders as a horizontally scrollable row on all screen sizes
 * so it never wraps and never eats vertical space.
 *
 * Each item is either:
 *   • `to`        — a real route (React Router <Link>)
 *   • `href`      — a plain <a> (external)
 *   • `onSelect`  — a callback (used for in-page filters)
 *
 * Only one of the three needs to be provided.
 */

export const STOREFRONT_ITEMS = [
  {
    id: "shop",
    label: "Shop",
    icon: Sparkles,
    to: "/marketplace",
    match: (pathname, params) => {
      if (pathname !== "/marketplace") return false;
      // Any sub-filter clears the "Shop" active state.
      return !params.get("filter");
    },
  },
  {
    id: "ready",
    label: "Ready to ship",
    icon: Truck,
    to: "/dropshipping",
    match: (pathname) => pathname === "/dropshipping",
  },
  {
    id: "ghana",
    label: "Available in Ghana",
    icon: BadgeCheck,
    to: "/marketplace?filter=ghana",
    match: (pathname, params) =>
      pathname === "/marketplace" && params.get("filter") === "ghana",
  },
  {
    id: "wholesale",
    label: "Wholesale",
    icon: Boxes,
    to: "/marketplace?filter=wholesale",
    match: (pathname, params) =>
      pathname === "/marketplace" && params.get("filter") === "wholesale",
  },
  {
    id: "preorder",
    label: "Pre-order windows",
    icon: Clock,
    to: "/preorder",
    match: (pathname) =>
      pathname === "/preorder" || pathname.startsWith("/preorder/"),
  },
  {
    id: "courses",
    label: "Digital Courses",
    icon: GraduationCap,
    to: "/courses",
    match: (pathname) =>
      pathname === "/courses" || pathname.startsWith("/courses/"),
  },
];

export default function StorefrontNav({ items = STOREFRONT_ITEMS }) {
  const { colors } = useTheme();
  const location = useLocation();
  const [params] = useSearchParams();

  return (
    <nav
      className="sf-nav"
      aria-label="Storefront sections"
      style={styles.nav(colors)}
    >
      {items.map((item) => {
        const active = item.match
          ? item.match(location.pathname, params)
          : false;
        const Icon = item.icon;

        return (
          <Link
            key={item.id}
            to={item.to}
            className="sf-nav-pill"
            aria-current={active ? "page" : undefined}
            style={styles.pill(colors, active)}
            onMouseEnter={(e) => {
              if (!active) e.currentTarget.style.borderColor = colors.btnBg;
            }}
            onMouseLeave={(e) => {
              if (!active) e.currentTarget.style.borderColor = colors.border;
            }}
          >
            <Icon size={14} />
            <span style={styles.label}>{item.label}</span>
            {item.badge && (
              <span style={styles.badge(colors)}>{item.badge}</span>
            )}
          </Link>
        );
      })}

      <style>{`
        .sf-nav {
          display: flex;
          align-items: center;
          gap: 8px;
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
          scroll-snap-type: x proximity;
          /* Left/right padding creates a breathing gutter at the ends */
          padding-left: 2px;
          padding-right: 2px;
          /* Small overscroll to hint scrollability on mobile */
          overscroll-behavior-x: contain;
        }
        .sf-nav::-webkit-scrollbar { display: none; }
        .sf-nav-pill {
          scroll-snap-align: start;
        }
        @media (max-width: 520px) {
          .sf-nav {
            /* Escape the parent's side padding so pills can scroll edge to edge */
            margin-left: -16px;
            margin-right: -16px;
            padding-left: 16px;
            padding-right: 16px;
          }
        }
      `}</style>
    </nav>
  );
}

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  nav: (c) => ({
    paddingTop: 2,
    paddingBottom: 2,
  }),
  pill: (c, active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
    padding: "8px 14px",
    borderRadius: 999,
    border: `1px solid ${active ? c.btnBg : c.border}`,
    background: active ? c.btnBg : c.raisedBg,
    color: active ? c.btnText : c.text,
    fontSize: 13,
    fontWeight: active ? 700 : 600,
    textDecoration: "none",
    whiteSpace: "nowrap",
    transition: "border-color 0.15s ease, background 0.15s ease",
    fontFamily: "inherit",
  }),
  label: {
    lineHeight: 1,
  },
  badge: (c) => ({
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    padding: "2px 6px",
    borderRadius: 999,
    background: c.warningBg,
    color: c.warning,
    marginLeft: 2,
  }),
};